import { Box, Button, SxProps, Theme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface ActionButtonsProps {
  onCancel: () => void;
  onSubmit?: () => void;
  onDelete?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  deleteLabel?: string;
  submitColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  submitVariant?: 'text' | 'outlined' | 'contained';
  cancelVariant?: 'text' | 'outlined' | 'contained';
  deleteVariant?: 'text' | 'outlined' | 'contained';
  submitIcon?: React.ReactNode;
  cancelIcon?: React.ReactNode;
  deleteIcon?: React.ReactNode;
  buttonOrder?: 'cancel-submit-delete' | 'submit-cancel-delete' | 'cancel-delete-submit';
  sx?: SxProps<Theme>;
}

/**
 * Action buttons set component
 * Common component for button sets (cancel, save, delete, etc.) used in various screens
 */
export const ActionButtons: React.FC<ActionButtonsProps> = ({
  onCancel,
  onSubmit,
  onDelete,
  submitLabel,
  cancelLabel,
  deleteLabel,
  submitColor = 'primary',
  submitVariant = 'contained',
  cancelVariant = 'outlined',
  deleteVariant = 'outlined',
  submitIcon,
  cancelIcon,
  deleteIcon,
  buttonOrder = 'cancel-submit-delete',
  sx,
}) => {
  const { t } = useTranslation('common');

  // Default button labels from translations
  const defaultSubmitLabel = t('buttons.save');
  const defaultCancelLabel = t('buttons.cancel');
  const defaultDeleteLabel = t('buttons.delete');

  const buttons = {
    cancel: (
      <Button
        type="button"
        variant={cancelVariant}
        onClick={onCancel}
        startIcon={cancelIcon}
      >
        {cancelLabel || defaultCancelLabel}
      </Button>
    ),
    submit: onSubmit && (
      <Button
        type="submit"
        variant={submitVariant}
        color={submitColor}
        onClick={onSubmit}
        startIcon={submitIcon}
      >
        {submitLabel || defaultSubmitLabel}
      </Button>
    ),
    delete: onDelete && (
      <Button
        type="button"
        variant={deleteVariant}
        color="error"
        onClick={onDelete}
        startIcon={deleteIcon}
      >
        {deleteLabel || defaultDeleteLabel}
      </Button>
    ),
  };

  // Button order
  const renderButtons = () => {
    switch (buttonOrder) {
      case 'submit-cancel-delete':
        return (
          <>
            {buttons.submit}
            {buttons.cancel}
            {buttons.delete}
          </>
        );
      case 'cancel-delete-submit':
        return (
          <>
            {buttons.cancel}
            {buttons.delete}
            {buttons.submit}
          </>
        );
      case 'cancel-submit-delete':
      default:
        return (
          <>
            {buttons.cancel}
            {buttons.submit}
            {buttons.delete}
          </>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: 2, mt: 2, ...sx }}>
      {renderButtons()}
    </Box>
  );
};

export default ActionButtons;
